import { useRender } from "@base-ui/react/use-render";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center text-sm font-medium transition-colors",
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:opacity-50 disabled:pointer-events-none",
    "active:scale-[.99]",
  ),
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-primary/90",
        outline: "text-primary border border-primary/50 hover:bg-black/5",
        secondary: "bg-white text-charcoal hover:bg-gray-100 hover:text-charcoal/90",
        ghost: "text-primary hover:text-primary/80 hover:bg-black/5",
        link: "underline-offset-4 hover:underline text-primary drop-shadow-none active:drop-shadow-none",
      },
      size: {
        default: "h-10 py-2 px-4",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 rounded-md",
        // icon sizes so default-registry items (dialog close, input-group, …) compose against this button
        icon: "h-10 w-10",
        "icon-sm": "h-9 w-9",
        "icon-xs": "h-6 w-6",
      },
      rounded: {
        default: "rounded-md",
        full: "rounded-full",
        none: "rounded-none",
      },
      elevation: {
        none: "drop-shadow-none",
        elevated: "drop-shadow-xl active:drop-shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
      elevation: "none",
    },
  },
);

export type ButtonProps = useRender.ComponentProps<"button"> & VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, elevation, render, ...props }, ref) => {
    // Default to type="button" so in-form actions do not submit. The render element's
    // props win the merge, so the fallback must carry the caller's `type` or it would
    // override type="submit" at form call sites that opt in.
    return useRender({
      render: render ?? <button type={props.type ?? "button"} />,
      ref,
      defaultTagName: "button",
      props: {
        "data-slot": "button",
        className: cn(buttonVariants({ variant, size, rounded, elevation, className })),
        ...props,
      },
    });
  },
);
Button.displayName = "Button";

export { Button };
